import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useCreateProject } from '@/hooks/useProjects';
import { useCreateStory, useSetCurrentStory } from '@/hooks/useStories';
import { useCreateEntity } from '@/services/query/entities-queries';
import { useMarkOnboardingFlowCompleted } from '@/services/query/feature-params-queries';
import { supabase } from '@/services/supabase/supabase';
import { templateService } from '@/services/template';
import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";

import { CompletionStep } from './components/steps/CompletionStep';
import { CreateEntityStep } from './components/steps/CreateEntityStep';
import { PlanBeatsStep } from './components/steps/PlanBeatsStep';
import { ProjectStorySetupStep } from "./components/steps/ProjectStorySetupStep";
import { TimelineTemplateStep } from './components/steps/TimelineTemplateStep';
import { WelcomeChoiceStep } from './components/steps/WelcomeChoiceStep';
import { STORY_TEMPLATES, StoryBeat } from '@/lib/storyTemplates';

export default function OnboardingFlowPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(-1); // Start with welcome choice step
  const [pageLoading, setPageLoading] = useState(true);

  // State for created items
  const [projectData, setProjectData] = useState({ name: '', description: '', genre: '' });
  const [storyData, setStoryData] = useState({ name: '', plotline: '', genre: '' });
  const [entityData, setEntityData] = useState<{
    name: string;
    type: string;
    details: Record<string, any>;
  }>({
    name: '',
    type: 'character',
    details: {}
  });
  const [timelineTemplate, setTimelineTemplate] = useState('hero');
  const [timelineName, setTimelineName] = useState('Main Timeline');

  // State for created item IDs
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);
  const [createdStoryId, setCreatedStoryId] = useState<string | null>(null);
  const [createdEntityId, setCreatedEntityId] = useState<string | null>(null);

  // Flow state
  const [flowType, setFlowType] = useState<'architect' | 'discovery' | 'student' | null>(null);
  const [beatsData, setBeatsData] = useState<any[]>([]);

  // Sync beats with template
  useEffect(() => {
    if (timelineTemplate && timelineTemplate !== 'empty' && STORY_TEMPLATES[timelineTemplate]) {
      const templateBeats = STORY_TEMPLATES[timelineTemplate].beats.map((beat: StoryBeat) => ({
        id: crypto.randomUUID(),
        title: beat.title,
        description: beat.description
      }));
      setBeatsData(templateBeats);
    } else if (timelineTemplate === 'empty') {
      setBeatsData([]);
    }
  }, [timelineTemplate]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);

  // Mutations
  const createProject = useCreateProject();
  const createStory = useCreateStory();
  const createEntity = useCreateEntity();
  const setCurrentStory = useSetCurrentStory();
  const markCompleted = useMarkOnboardingFlowCompleted();

  // Start at the welcome choice step and set loading to false
  useEffect(() => {
    setCurrentStep(-1); // Start with welcome choice
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Handle combined project and story creation
  const handleCreateProjectAndStory = async () => {
    if (!projectData.name) {
      toast({
        title: "Title required",
        description: "Please enter a title for your project",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const projectResult = await createProject.mutateAsync({
        name: projectData.name,
        description: projectData.description || null,
        genre: projectData.genre || null
      });

      const projectId = projectResult.id;
      setCreatedProjectId(projectId);

      const storyResult = await createStory.mutateAsync({
        project_id: projectId,
        name: storyData.name || projectData.name,
        plotline: storyData.plotline || undefined,
        genre: storyData.genre || undefined
      });

      const storyId = storyResult.id;
      setCreatedStoryId(storyId);

      await setCurrentStory.mutateAsync({
        project_id: projectId,
        story_id: storyId
      });

      setCurrentStep(1);
      toast({
        title: "Setup complete!",
        description: "Your project and story have been created.",
      });
    } catch (error) {
      console.error('Error during setup:', error);
      toast({
        title: "Error during setup",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle timeline template selection
  const handleSelectTimelineTemplate = async () => {
    if (!timelineName || !createdStoryId) {
      toast({
        title: "Timeline information required",
        description: "Please ensure you have selected a template and entered a name",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await templateService.createTimelineFromTemplate(
        timelineTemplate,
        timelineName,
        `Created during onboarding with the ${timelineTemplate} template`,
        createdStoryId
      );

      if (!result.success || !result.timeline) {
        throw new Error(result.error || "Failed to create timeline");
      }

      setCurrentStep(2);
      toast({
        title: "Timeline created!",
        description: `Your story now has a timeline with the ${timelineTemplate} template`,
      });
    } catch (error) {
      console.error('Error creating timeline:', error);
      toast({
        title: "Error creating timeline",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ... helper for tenant ID (extracted for clarity)
  async function getTenantId() {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.app_metadata?.tenant_id;
  }

  // Handle entity creation
  const handleCreateEntity = async () => {
    if (!entityData.name || !createdProjectId) {
      toast({
        title: "Character name required",
        description: "Please enter a name for your character",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const tenantId = await getTenantId();

      // 1. Create/Ensure Character Category
      const { data: categoryData, error: categoryError } = await supabase
        .from('template_categories')
        .insert([{
          name: 'Characters',
          description: 'The people and protagonists in your story',
          project_id: createdProjectId,
          tenant_id: tenantId,
          icon: 'Users'
        }])
        .select().single();

      if (categoryError && !categoryError.message.includes('unique')) throw categoryError;

      // 2. Create Character Template with standard fields
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .insert([{
          name: 'Main Character Template',
          description: 'A detailed template for story protagonists',
          project_id: createdProjectId,
          tenant_id: tenantId
        }])
        .select().single();

      if (templateError) throw templateError;

      // 3. Create Template Section
      await supabase
        .from('template_sections')
        .insert([{
          template_id: templateData.id,
          name: 'Identity & Motivation',
          order_position: 1,
          tenant_id: tenantId
        }]);

      // Mirror to form_sections for UI compatibility
      await supabase.from('form_sections').insert([{
        template_id: templateData.id,
        name: 'Identity & Motivation',
        order_position: 1,
        tenant_id: tenantId
      }]);

      // 4. Create the Entity
      const newEntity = await createEntity.mutateAsync({
        projectId: createdProjectId,
        data: {
          name: entityData.name,
          description: entityData.details.description || '',
          status: 'active',
          template_id: templateData.id,
          category_id: categoryData.id,
          field_values: {
            role: entityData.details.role || '',
            motivation: entityData.details.description || ''
          }
        }
      });

      setCreatedEntityId(newEntity.id.toString());

      // Advance step
      setCurrentStep(3);

      toast({ title: "Character created!", description: `${entityData.name} is ready for your story.` });
    } catch (error) {
      console.error('Error creating entity:', error);
      toast({ title: "Error creating entity", description: "Please try again", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateBeats = async () => {
    if (!createdStoryId || beatsData.length === 0) {
      setCurrentStep(4);
      return;
    }

    setIsLoading(true);
    try {
      const { data: timeline } = await supabase.from('timelines').select('id').eq('story_id', createdStoryId).eq('is_primary', true).single();
      if (timeline) {
        const { data: cards } = await supabase.from('plot_cards').select('id').eq('timeline_id', timeline.id).order('id', { ascending: true });
        if (cards && cards.length > 0) {
          const updates = beatsData.map((beat, index) => {
            const cardId = cards[index]?.id;
            if (cardId) return supabase.from('plot_cards').update({ title: beat.title, content: beat.description }).eq('id', cardId);
            return null;
          }).filter(Boolean);
          await Promise.all(updates);
        }
      }
      setCurrentStep(4);
      toast({ title: "Beats updated!", description: "Your story structure is ready." });
    } catch (error) {
      console.error('Error updating beats:', error);
      setCurrentStep(4);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnboardingComplete = async () => {
    setIsLoading(true);
    try {
      await markCompleted.mutateAsync();
      toast({ title: "Onboarding completed!", description: "You're all set to start writing" });
      if (createdProjectId) navigate(`/projects/${createdProjectId}/overview`);
      else navigate('/workspace');
    } catch (error) {
      console.error('Error completion:', error);
      navigate('/workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (!createdProjectId || !createdStoryId) handleCreateProjectAndStory();
      else setCurrentStep(1);
    } else if (currentStep === 1) {
      handleSelectTimelineTemplate();
    } else if (currentStep === 2) {
      handleCreateEntity();
    } else if (currentStep === 3) {
      if (flowType === 'architect') handleUpdateBeats();
      else handleOnboardingComplete();
    } else if (currentStep === 4) {
      handleOnboardingComplete();
    }
  };

  const handleBack = () => { if (currentStep > -1) setCurrentStep(currentStep - 1); };

  const handleExit = async () => {
    try {
      await markCompleted.mutateAsync();
      toast({ title: "Onboarding skipped" });
      navigate('/workspace');
    } catch (error) { navigate('/workspace'); }
  };

  const renderStep = () => {
    switch (currentStep) {
      case -1: return <WelcomeChoiceStep onPlan={() => { setFlowType('architect'); setCurrentStep(0); }} onWrite={() => { setFlowType('discovery'); setCurrentStep(0); }} onStartingOut={() => { setFlowType('student'); setCurrentStep(0); }} onExit={handleExit} />;
      case 0: return <ProjectStorySetupStep projectData={projectData} setProjectData={setProjectData} storyData={storyData} setStoryData={setStoryData} />;
      case 1: return <TimelineTemplateStep selectedTemplate={timelineTemplate} setSelectedTemplate={setTimelineTemplate} storyId={createdStoryId} timelineName={timelineName} setTimelineName={setTimelineName} />;
      case 2: return <CreateEntityStep entityData={entityData} setEntityData={setEntityData} storyId={createdStoryId} projectId={createdProjectId} />;
      case 3: return flowType === 'architect' ? <PlanBeatsStep beats={beatsData} onBeatsChange={setBeatsData} /> : <CompletionStep projectId={createdProjectId} storyId={createdStoryId} projectName={projectData.name} storyName={storyData.name} characterName={entityData.name} structureName={STORY_TEMPLATES[timelineTemplate]?.name || "No Structure Selected"} beats={beatsData} />;
      case 4: return <CompletionStep projectId={createdProjectId} storyId={createdStoryId} projectName={projectData.name} storyName={storyData.name} characterName={entityData.name} structureName={STORY_TEMPLATES[timelineTemplate]?.name || "No Structure Selected"} beats={beatsData} />;
      default: return null;
    }
  };

  const getButtonText = () => {
    if (isLoading) return "Processing...";
    switch (currentStep) {
      case 0: return "Next — choose structure";
      case 1: return "Confirm story structure";
      case 2: return createdEntityId ? "Continue" : "Establish character";
      case 3: return flowType === 'architect' ? "Confirm Beats" : "Finish setup";
      case 4: return "Finish setup";
      default: return "Next";
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-muted-foreground">Loading onboarding guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 flex flex-col justify-center items-center container px-6 py-12">
        <div className="w-full max-w-2xl mx-auto">
          {currentStep >= 0 && (
            <div className="w-full max-w-xl mx-auto mb-12 animate-in fade-in duration-700">
              <div className="flex gap-2 w-full">
                {Array.from({ length: flowType === 'architect' ? 5 : 4 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-[2px] flex-1 rounded-full transition-all duration-500",
                      i <= currentStep ? "bg-primary opacity-90" : "bg-muted/40"
                    )}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={cn("animate-in fade-in duration-700 slide-in-from-bottom-4 w-full max-w-xl mx-auto")}>
            {renderStep()}
          </div>

          {currentStep >= 0 && (
            <div className="mt-12 w-full max-w-xl mx-auto space-y-8 animate-in fade-in duration-700 delay-300">
              <div className="flex flex-col items-center w-full space-y-6">
                <Button size="lg" onClick={handleNext} disabled={isLoading} className="w-full h-14 text-base font-bold rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl shadow-primary/10 transition-all active:scale-[0.98]">
                  {isLoading ? "Processing..." : getButtonText()}
                  {!isLoading && <ArrowRight className="ml-2 h-5 w-5 opacity-80" />}
                </Button>
                <div className="w-full h-px bg-border/40" />
                <div className="w-full flex items-center justify-between px-2">
                  <button onClick={handleBack} disabled={currentStep === 0 || isLoading} className={cn("text-xs font-semibold text-muted-foreground/60 hover:text-foreground transition-colors flex items-center gap-1.5", currentStep === 0 && "invisible pointer-events-none")}>← Back</button>
                  <button onClick={() => { if (currentStep === 1) { setTimelineTemplate('empty'); handleNext(); } else toast({ title: "Coming soon" }); }} className="text-xs font-semibold text-muted-foreground/60 hover:text-foreground transition-colors border-b border-muted-foreground/30 pb-0.5">
                    {currentStep === 1 ? "Skip structure for now" : "Walk me through it instead"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
